<script setup lang="ts">
import { reactive, ref, watch } from 'vue'
import { NButton, NInput, NSpin, useMessage } from 'naive-ui'
import { useCvProfileStore, type CvProfile, type CvProfileFields } from '../stores/cv_profile'
import { useFormattedDate } from '../composables/useFormattedDate'

// The Live CV contact block: the owner fields every rendered CV is built
// against, typed once and never touched by regeneration. Owns its draft
// copy of the profile and saves it through the profile store.
const store = useCvProfileStore()
const message = useMessage()
const { formatAbsoluteDate } = useFormattedDate()

interface Field {
  key: keyof CvProfileFields
  label: string
  placeholder: string
  hint?: string
}

const FIELDS: Field[] = [
  { key: 'name', label: 'Name', placeholder: 'Full name as it should appear on the CV' },
  { key: 'location', label: 'Location', placeholder: 'Oslo, Norway' },
  { key: 'email', label: 'Email', placeholder: 'you@example.com' },
  { key: 'linkedin_url', label: 'LinkedIn', placeholder: 'https://www.linkedin.com/in/…' },
  { key: 'github_url', label: 'GitHub', placeholder: 'https://github.com/…' },
  {
    key: 'birth_date',
    label: 'Birth date',
    placeholder: 'YYYY-MM-DD',
    hint: 'Stored to compute your age. The date itself is never shown on the CV.',
  },
]

const draft = reactive<Record<keyof CvProfileFields, string>>({
  name: '',
  location: '',
  email: '',
  linkedin_url: '',
  github_url: '',
  birth_date: '',
})
const saving = ref(false)

function loadDraft(profile: CvProfile): void {
  for (const field of FIELDS) draft[field.key] = profile[field.key] ?? ''
}

watch(() => store.profile, loadDraft, { immediate: true })

function toFields(): CvProfileFields {
  const fields = {} as CvProfileFields
  for (const field of FIELDS) fields[field.key] = draft[field.key].trim() || null
  return fields
}

async function save(): Promise<void> {
  saving.value = true
  try {
    await store.save(toFields())
    message.success('Profile saved')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to save profile')
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <section
    class="flex flex-col gap-2.5 rounded-2xl border border-border-subtle bg-surface-raised p-4"
    aria-labelledby="cv-contact-heading"
  >
    <h2 id="cv-contact-heading" class="m-0 text-sm font-semibold text-ink">Contact block</h2>
    <p class="m-0 text-xs text-ink-faint">Enter it once; regenerating the CV from the brain never touches these.</p>

    <div v-if="store.loading" class="flex items-center justify-center py-8" data-testid="cv-profile-loading">
      <NSpin />
    </div>

    <div v-else-if="store.loadError" class="text-sm text-status-alert" data-testid="cv-profile-load-error">
      Failed to load the CV profile: {{ store.loadError }}
    </div>

    <form v-else class="flex flex-col gap-2.5" data-testid="cv-profile-form" @submit.prevent="save">
      <label v-for="field in FIELDS" :key="field.key" class="flex flex-col gap-1 text-[13px] text-ink-muted">
        {{ field.label }}
        <NInput
          v-model:value="draft[field.key]"
          :placeholder="field.placeholder"
          :disabled="saving"
          :data-testid="`cv-profile-${field.key}`"
        />
        <span v-if="field.hint" class="text-xs text-ink-faint">{{ field.hint }}</span>
      </label>

      <div class="flex flex-wrap items-center gap-3 pt-1">
        <NButton attr-type="submit" :loading="saving" data-testid="cv-profile-save">Save profile</NButton>
        <span v-if="store.profile.updated_at" class="text-xs text-ink-faint" data-testid="cv-profile-updated-at">
          Last saved {{ formatAbsoluteDate(store.profile.updated_at) }}
        </span>
      </div>
    </form>
  </section>
</template>
